import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Grid, Autocomplete, CircularProgress, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuSection from "../components/MenuSection";
import { useTheme } from "@mui/material/styles";
import { vehiculosByCliente } from "../api/getVehiculosByCliente";
import Choferes from "../api/choferes";

const DatosChofer = () => {
  const esDUO = JSON.parse(localStorage.getItem("esDUO")) || false;
  const navigate = useNavigate();
  const theme = useTheme();

  // Función helper para parsear localStorage
  const parseLocalStorageItem = key => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : {};
    } catch (error) {
      console.error(`Error parsing localStorage item "${key}":`, error);
      return {};
    }
  };

  const cliente = parseLocalStorageItem("cliente");
  const choferFromStorage = parseLocalStorageItem("chofer");

  // Estados para chofer
  const [chofer, setChofer] = useState(choferFromStorage);
  const [searchTermChofer, setSearchTermChofer] = useState(choferFromStorage.nombreChofer || "");
  const [choferes, setChoferes] = useState([]);
  const [isLoadingChoferes, setIsLoadingChoferes] = useState(false);
  const [noChoferes, setNoChoferes] = useState(false);
  const [choferesNoDisponibles, setChoferesNoDisponibles] = useState(false);

  // Estados existentes para vehículo
  const [numeroVehiculo, setNumeroVehiculo] = useState(choferFromStorage.numeroVehiculo || "");
  const [patente, setPatente] = useState(choferFromStorage.patente || "");
  const [kilometraje, setKilometraje] = useState(choferFromStorage.kilometraje || "");
  const [vehiculos, setVehiculos] = useState([]);
  const [modalWidth, setModalWidth] = useState(window.innerWidth < 700 ? "360px" : "900px");

  // Fetch inicial de choferes
  useEffect(() => {
    const fetchChoferes = async () => {
      setIsLoadingChoferes(true);
      try {
        const response = await Choferes.getChoferes("");
        const choferesData = Object.values(response.data.choferes || {});
        setChoferes(choferesData);
        setNoChoferes(choferesData.length === 0);
        setChoferesNoDisponibles(false);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setNoChoferes(true);
          setChoferes([]);
          setChoferesNoDisponibles(true);
        } else {
          setNoChoferes(false);
          setChoferes([]);
          setChoferesNoDisponibles(false);
        }
        console.error("Error al obtener choferes:", error);
      } finally {
        setIsLoadingChoferes(false);
      }
    };

    fetchChoferes();
  }, []);

  // Fetch de vehículos (código existente)
  useEffect(() => {
    const fetchVehiculos = async () => {
      try {
        const idCliente = cliente.idCliente;
        const response = await vehiculosByCliente(idCliente);
        setVehiculos(response.data || []);
      } catch (error) {
        console.error("Error al obtener los vehículos del cliente:", error);
      }
    };

    fetchVehiculos();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setModalWidth(window.innerWidth < 700 ? "360px" : "900px");
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleChoferSelect = event => {
    const idChoferSeleccionado = event.target.value;
    const choferSeleccionado = choferes.find(c => c.idChofer === idChoferSeleccionado);

    if (choferSeleccionado) {
      // Chofer seleccionado de la lista
      setChofer(choferSeleccionado);
      setSearchTermChofer(choferSeleccionado.nombreChofer);
    }
  };

  const handleAgregar = () => {
    const updatedChoferData = {
      ...chofer,
      nombreChofer: chofer.nombreChofer || searchTermChofer.trim(),
      numeroVehiculo: numeroVehiculo.toUpperCase(),
      patente: patente.toUpperCase(),
      kilometraje,
    };

    localStorage.setItem("chofer", JSON.stringify(updatedChoferData));
    localStorage.setItem("completoDatosChofer", true);
    navigate("/dashboard");
  };

  const botonAceptarDeshabilitado = () => {
    const requiereChofer = cliente.exigeChofer ? !chofer.nombreChofer && searchTermChofer.trim() === "" : false;
    const requiereVehiculo = cliente.exigeVehiculo ? numeroVehiculo === "" : false;
    const requierePatente = cliente.exigePatente ? patente === "" : false;
    const requiereKilometraje = cliente.exigeKm ? kilometraje === "" : false;

    return requiereChofer || requiereVehiculo || requierePatente || requiereKilometraje;
  };

  const handleVehiculoSelect = (event, value) => {
    if (value) {
      setNumeroVehiculo(value.numero);
      setPatente(value.patente);
    } else {
      setNumeroVehiculo("");
      setPatente("");
    }
  };

  return (
    <>
      <MenuSection />

      <Box
        sx={{
          position: "absolute",
          width: modalWidth,
          top: window.innerWidth < 700 ? "49%" : esDUO? "60%" : "54%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          p: 4,
          borderRadius: "8px",
          height: window.innerWidth < 700 ? "75vh" : "90%",
          flexDirection: "column",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {isLoadingChoferes ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress sx={{ color: theme.bandera.colorSecundario }} />
              </Box>
            ) : noChoferes || choferes.length === 0 ? (
              <TextField
                label="Nombre del chofer"
                value={searchTermChofer}
                onChange={e => setSearchTermChofer(e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{
                  style: { fontSize: "0.85rem", color: "gray" },
                }}
              />
            ) : (
              <FormControl fullWidth>
                <Autocomplete
                  value={chofer.nombreChofer ? chofer : null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setChofer(newValue);
                      setSearchTermChofer(newValue.nombreChofer);
                    } else {
                      setChofer({});
                      setSearchTermChofer("");
                    }
                  }}
                  options={choferes}
                  getOptionLabel={option => option.nombreChofer || ""}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Seleccionar Chofer"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{
                        style: { fontSize: "0.85rem", color: "gray" },
                      }}
                    />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option => option.nombreChofer.toLowerCase().includes(inputValue.toLowerCase()));
                  }}
                />
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {vehiculos.length > 0 ? (
              <Autocomplete
                value={vehiculos.find(v => v.numero === numeroVehiculo) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setNumeroVehiculo(newValue.numero);
                    setPatente(newValue.patente);
                  } else {
                    setNumeroVehiculo("");
                    setPatente("");
                  }
                }}
                options={vehiculos}
                getOptionLabel={option => option.numero + " - " + option.patente}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Seleccionar Vehículo"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      style: { fontSize: "0.85rem", color: "gray" },
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                label="N° vehículo"
                value={numeroVehiculo}
                onChange={e => setNumeroVehiculo(e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{
                  style: { fontSize: "0.85rem", color: "gray" },
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Patente"
              value={patente}
              onChange={e => setPatente(e.target.value)}
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: vehiculos.length > 0, // La patente es de solo lectura si hay vehículos disponibles
              }}
              InputLabelProps={{
                style: { fontSize: "0.85rem", color: "gray" },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Kilometraje"
              value={kilometraje}
              onChange={e => setKilometraje(e.target.value)}
              variant="outlined"
              fullWidth
              InputLabelProps={{
                style: { fontSize: "0.85rem", color: "gray" },
              }}
              type="number"
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: window.innerWidth < 700 ? "center" : "flex-end",
            width: "100%",
            maxWidth: "300px",
            marginTop: "120px",
            boxSizing: "border-box",
          }}
        >
          <Button
            variant="contained"
            sx={{
              boxShadow: 0,
              fontWeight: "bold",
              fontSize: 17,
              marginTop: "19px",
              color:"black",
              backgroundColor: theme.bandera.colorSecundario,
              ":hover": {
                backgroundColor: theme.bandera.colorSecundario,
              },
            }}
            onClick={handleCancel}
          >
            CANCELAR
          </Button>
          <Button
            variant="contained"
            sx={{
              boxShadow: 0,
              fontWeight: "bold",
              marginLeft: "20px",
              fontSize: 17,
              marginTop: "19px",
              color:"black",
              backgroundColor: theme.bandera.colorSecundario,
              ":hover": {
                backgroundColor: theme.bandera.colorSecundario,
              },
            }}
            onClick={handleAgregar}
            disabled={botonAceptarDeshabilitado()}
          >
            ACEPTAR
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default DatosChofer;
